import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Briefcase, CheckCircle2, Clock, DollarSign, Sparkles } from "lucide-react";
import MatchedJobsFeed from "@/components/MatchedJobsFeed";

export default function DashboardPage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [loading, user, nav]);

  useEffect(() => {
    if (user) api.get("/me/jobs").then((r) => setJobs(r.data));
  }, [user]);

  if (!user) return null;

  if (user.role === "homeowner") return <HomeownerDashboard user={user} jobs={jobs} />;
  return <TradespersonDashboard user={user} jobs={jobs} />;
}

function StatusBadge({ status }) {
  const label = {
    open: "Open",
    in_progress: "Quote Accepted",
    completed: "Work Completed",
    closed: "Closed",
  }[status] || status.replace("_", " ");
  const s = {
    open: "bg-emerald-50 text-emerald-700 border-emerald-200",
    in_progress: "bg-blue-50 text-blue-900 border-blue-200",
    completed: "bg-slate-100 text-slate-700 border-slate-200",
    closed: "bg-slate-100 text-slate-500 border-slate-200",
  }[status] || "bg-slate-100 text-slate-700";
  return <Badge className={`${s} border font-normal`}>{label}</Badge>;
}

function JobRow({ j }) {
  return (
    <Link to={`/jobs/${j.id}`} data-testid={`dash-job-${j.id}`} className="block">
      <div className="p-4 rounded-lg border border-slate-200 bg-white tf-card-hover">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium text-slate-900 truncate">{j.title}</div>
            <div className="text-xs text-slate-500 mt-1">
              {j.city} • {j.category} • {(j.quotes || []).length} quote{(j.quotes || []).length === 1 ? "" : "s"}
            </div>
          </div>
          <StatusBadge status={j.status} />
        </div>
      </div>
    </Link>
  );
}

function HomeownerDashboard({ user, jobs }) {
  const open = jobs.filter((j) => j.status === "open");
  const active = jobs.filter((j) => j.status === "in_progress");
  const past = jobs.filter((j) => j.status === "completed");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2">Homeowner</div>
          <h1 className="font-display font-bold text-3xl text-slate-900">Welcome, {user.name.split(" ")[0]}</h1>
        </div>
        <Link to="/post-job"><Button data-testid="dash-post-job" className="bg-orange-600 hover:bg-orange-700 text-white">Post a Job</Button></Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Briefcase} label="Open Jobs" value={open.length} testId="stat-open" />
        <StatCard icon={Clock} label="In Progress" value={active.length} testId="stat-active" />
        <StatCard icon={CheckCircle2} label="Completed" value={past.length} testId="stat-past" />
        <StatCard icon={DollarSign} label="Total Posted" value={jobs.length} testId="stat-total" />
      </div>

      <Tabs defaultValue="open">
        <TabsList>
          <TabsTrigger value="open" data-testid="tab-open">Open ({open.length})</TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active">In Progress ({active.length})</TabsTrigger>
          <TabsTrigger value="past" data-testid="tab-past">Completed ({past.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="open"><JobList items={open} empty="No open jobs yet." /></TabsContent>
        <TabsContent value="active"><JobList items={active} empty="No active jobs." /></TabsContent>
        <TabsContent value="past"><JobList items={past} empty="No completed jobs yet." /></TabsContent>
      </Tabs>
    </div>
  );
}

function TradespersonDashboard({ user, jobs }) {
  const applied = jobs.filter((j) => j.status === "open");
  const active = jobs.filter((j) => j.status === "in_progress" && j.accepted_tradesperson_id === user.id);
  const completed = jobs.filter((j) => j.status === "completed" && j.accepted_tradesperson_id === user.id);

  const earnings = completed.reduce((sum, j) => {
    const q = (j.quotes || []).find((x) => x.id === j.accepted_quote_id);
    return sum + (q?.price || 0);
  }, 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2">Tradesperson</div>
          <h1 className="font-display font-bold text-3xl text-slate-900">Hi {user.name.split(" ")[0]}</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/profile/edit"><Button data-testid="dash-edit-profile" variant="outline" className="border-blue-900 text-blue-900">Edit Profile</Button></Link>
          <Link to="/jobs"><Button data-testid="dash-browse-jobs" className="bg-orange-600 hover:bg-orange-700 text-white">Browse Jobs</Button></Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Briefcase} label="Quotes Sent" value={applied.length} testId="stat-quotes" />
        <StatCard icon={Clock} label="Active Jobs" value={active.length} testId="stat-active" />
        <StatCard icon={CheckCircle2} label="Completed" value={completed.length} testId="stat-completed" />
        <StatCard icon={DollarSign} label="Total Earned" value={`$${earnings.toLocaleString()}`} testId="stat-earnings" />
      </div>

      <Tabs defaultValue="matched">
        <TabsList>
          <TabsTrigger value="matched" data-testid="tab-matched">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Matched Jobs
          </TabsTrigger>
          <TabsTrigger value="applied" data-testid="tab-applied">Quoted ({applied.length})</TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active">Active ({active.length})</TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">Completed ({completed.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="matched"><MatchedJobsFeed /></TabsContent>
        <TabsContent value="applied"><JobList items={applied} empty="You haven't quoted on any open jobs yet." /></TabsContent>
        <TabsContent value="active"><JobList items={active} empty="No active jobs." /></TabsContent>
        <TabsContent value="completed"><JobList items={completed} empty="No completed jobs yet." /></TabsContent>
      </Tabs>
    </div>
  );
}

function JobList({ items, empty }) {
  if (items.length === 0) return <div className="text-slate-500 text-sm py-8" data-testid="dash-empty">{empty}</div>;
  return <div className="space-y-3 mt-4">{items.map((j) => (<JobRow key={j.id} j={j} />))}</div>;
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <Card className="border-slate-200" data-testid={testId}>
      <CardContent className="p-5">
        <div className="w-9 h-9 rounded-md bg-blue-50 text-blue-900 grid place-items-center"><Icon className="w-4 h-4" /></div>
        <div className="font-display font-bold text-2xl text-slate-900 mt-3">{value}</div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{label}</div>
      </CardContent>
    </Card>
  );
}
